import { useEffect, useRef } from 'react'

interface Props {
  phase:      'idle' | 'betting' | 'flying' | 'crashed'
  multiplier: number
  countdown:  number
  onCrashComplete?: () => void
}

export function PlaneCanvas({ phase, multiplier, countdown, onCrashComplete }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stateRef  = useRef({ phase, multiplier, countdown })
  const rafRef    = useRef<number>()
  const dataRef   = useRef({
    gt: 0, lastT: 0,
    trail:     [] as {x:number;y:number}[],
    particles: [] as {x:number;y:number;vx:number;vy:number;life:number;decay:number;r:number;col:string}[],
    smoke:     [] as {x:number;y:number;vx:number;vy:number;life:number;decay:number;r:number;g:number}[],
    stars:     [] as {x:number;y:number;r:number;tw:number;sp:number}[],
    clouds:    [] as {x:number;y:number;w:number;h:number;sp:number;a:number}[],
    planeX: 0, planeY: 0, planeAngle: 0,
    crashPhase: 'none' as 'none'|'sputter'|'flyaway',
    sputterT: 0, flyawayT: 0, redFlash: 0,
    crashStartX: 0, crashStartY: 0, crashStartAngle: 0,
    crashCompleteFired: false,
  })

  // sync state changes
  useEffect(() => {
    const prev = stateRef.current
    const d    = dataRef.current
    if (prev.phase !== 'flying' && phase === 'flying') {
      d.gt = 0; d.trail = []; d.crashPhase = 'none'; d.sputterT = 0; d.flyawayT = 0; d.redFlash = 0; d.crashCompleteFired = false
    }
    if (prev.phase === 'flying' && phase === 'crashed') {
      d.crashPhase = 'sputter'; d.sputterT = 0; d.flyawayT = 0; d.crashCompleteFired = false
    }
    if (phase === 'betting' || phase === 'idle') {
      d.gt = 0; d.trail = []; d.particles = []; d.smoke = []; d.crashPhase = 'none'
      d.sputterT = 0; d.flyawayT = 0; d.redFlash = 0; d.crashCompleteFired = false
    }
    stateRef.current = { phase, multiplier, countdown }
  }, [phase, multiplier, countdown])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const d   = dataRef.current

    const resize = () => {
      canvas.width  = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      initStars(); initClouds()
    }

    const initStars = () => {
      d.stars = []
      for (let i = 0; i < 70; i++) d.stars.push({
        x: Math.random()*canvas.width, y: Math.random()*canvas.height*0.7,
        r: Math.random()*1.4+0.3, tw: Math.random()*Math.PI*2, sp: Math.random()*0.02+0.005
      })
    }

    const initClouds = () => {
      d.clouds = []
      for (let i = 0; i < 5; i++) d.clouds.push({
        x: Math.random()*canvas.width,
        y: canvas.height*0.25+Math.random()*canvas.height*0.45,
        w: 60+Math.random()*90, h: 18+Math.random()*22,
        sp: 0.3+Math.random()*0.4, a: 0.03+Math.random()*0.04
      })
    }

    const getPos = (t: number, W: number, H: number) => {
      const p = Math.min(t/25, 1), e = 1-Math.pow(1-p,2)
      return { x: W*0.1+e*W*0.78, y: H*0.82-e*H*0.7, a: -Math.atan2(H*0.7,W*0.78)*(1-p*0.3) }
    }

    const spawnSmoke = (x: number, y: number, n: number) => {
      for (let i = 0; i < n; i++) d.smoke.push({
        x: x+(Math.random()-0.5)*20, y: y+(Math.random()-0.5)*10,
        vx: (Math.random()-0.7)*1.5, vy: -Math.random()*1.5-0.5,
        life: 1, decay: 0.007+Math.random()*0.005,
        r: 8+Math.random()*12, g: 100+Math.floor(Math.random()*60)
      })
    }

    const spawnExplosion = (x: number, y: number) => {
      for (let i = 0; i < 40; i++) {
        const a = Math.random()*Math.PI*2, sp = Math.random()*9+3
        d.particles.push({
          x, y, vx: Math.cos(a)*sp, vy: Math.sin(a)*sp-3,
          life: 1, decay: 0.018+Math.random()*0.018, r: Math.random()*5+2,
          col: ['#ef4444','#f97316','#fbbf24','#ffffff','#fb923c','#fde047'][Math.floor(Math.random()*6)]
        })
      }
    }

    const drawPlane = (x: number, y: number, angle: number, flameScale: number, shakeAmt: number, now: number) => {
      ctx.save()
      ctx.translate(x+(Math.random()-0.5)*shakeAmt, y+(Math.random()-0.5)*shakeAmt)
      ctx.rotate(angle+(Math.random()-0.5)*shakeAmt*0.05)
      ctx.scale(0.55, 0.55)

      if (flameScale > 0) {
        const g = ctx.createRadialGradient(0,0,0,0,0,80)
        g.addColorStop(0,'rgba(34,197,94,0.3)'); g.addColorStop(1,'transparent')
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0,0,80,0,Math.PI*2); ctx.fill()
      }

      if (flameScale > 0.01) {
        ctx.save(); ctx.translate(-60,0)
        const fl = flameScale*(1+Math.sin(now*0.05)*0.2)
        ctx.scale(fl, flameScale)
        const eg = ctx.createRadialGradient(-60,0,0,0,0,70)
        eg.addColorStop(0,'rgba(255,245,157,1)'); eg.addColorStop(0.2,'rgba(255,152,0,0.95)')
        eg.addColorStop(0.5,'rgba(255,87,34,0.7)'); eg.addColorStop(1,'rgba(211,47,47,0)')
        ctx.fillStyle = eg
        ctx.beginPath(); ctx.ellipse(-60,0,70,18,0,0,Math.PI*2); ctx.fill()
        ctx.beginPath(); ctx.ellipse(-40,0,50,12,0,0,Math.PI*2); ctx.fill()
        ctx.fillStyle='rgba(255,253,231,0.95)'; ctx.beginPath(); ctx.ellipse(-20,0,25,7,0,0,Math.PI*2); ctx.fill()
        ctx.fillStyle='rgba(255,255,255,0.8)'; ctx.beginPath(); ctx.ellipse(-10,0,15,4,0,0,Math.PI*2); ctx.fill()
        ctx.restore()
      }

      const bg = ctx.createLinearGradient(0,0,100,0)
      bg.addColorStop(0,'#d81b1b'); bg.addColorStop(1,'#a01010')
      ctx.fillStyle = bg; ctx.strokeStyle = '#6d0808'; ctx.lineWidth = 1.5
      ctx.beginPath(); ctx.moveTo(0,0); ctx.lineTo(85,0)
      ctx.quadraticCurveTo(92,-2,95,-8); ctx.lineTo(100,-5); ctx.lineTo(98,2)
      ctx.quadraticCurveTo(95,8,85,10); ctx.lineTo(15,10)
      ctx.quadraticCurveTo(5,8,0,0); ctx.closePath(); ctx.fill(); ctx.stroke()

      ctx.beginPath(); ctx.moveTo(8,0); ctx.lineTo(0,-2); ctx.lineTo(-8,-25)
      ctx.lineTo(-2,-27); ctx.lineTo(5,-5); ctx.lineTo(8,0)
      ctx.closePath(); ctx.fill(); ctx.stroke()

      ctx.fillStyle='#c91818'
      ctx.beginPath(); ctx.moveTo(35,5); ctx.lineTo(20,-15); ctx.lineTo(55,-15)
      ctx.lineTo(60,-10); ctx.lineTo(50,5); ctx.lineTo(35,5)
      ctx.closePath(); ctx.fill(); ctx.stroke()

      ctx.fillStyle='rgba(26,26,26,0.9)'; ctx.beginPath(); ctx.ellipse(70,-2,12,6,0,0,Math.PI*2); ctx.fill()
      ctx.fillStyle='rgba(79,195,247,0.4)'; ctx.beginPath(); ctx.ellipse(72,-3,6,3,0,0,Math.PI*2); ctx.fill()

      ctx.fillStyle='#8b0000'; ctx.strokeStyle='#4a0000'; ctx.lineWidth=1.5
      ctx.beginPath(); ctx.arc(100,0,8,0,Math.PI*2); ctx.fill(); ctx.stroke()
      ctx.fillStyle='#2c2c2c'; ctx.beginPath(); ctx.arc(100,0,4,0,Math.PI*2); ctx.fill()

      ctx.save(); ctx.translate(100,0)
      ctx.rotate((now*0.03)%(Math.PI*2))
      ctx.fillStyle='rgba(26,26,26,0.55)'
      ctx.beginPath(); ctx.ellipse(0,-22,2.5,22,0,0,Math.PI*2); ctx.fill()
      ctx.beginPath(); ctx.ellipse(0,22,2.5,22,0,0,Math.PI*2); ctx.fill()
      ctx.beginPath(); ctx.ellipse(-22,0,22,2.5,0,0,Math.PI*2); ctx.fill()
      ctx.beginPath(); ctx.ellipse(22,0,22,2.5,0,0,Math.PI*2); ctx.fill()
      ctx.restore()
      ctx.fillStyle='#3e2723'; ctx.beginPath(); ctx.arc(100,0,3,0,Math.PI*2); ctx.fill()

      ctx.strokeStyle='rgba(255,82,82,0.9)'; ctx.lineWidth=2
      ctx.beginPath(); ctx.moveTo(20,3); ctx.quadraticCurveTo(50,2,80,3); ctx.stroke()

      ctx.restore()
    }

    const drawTrail = (crashed: boolean) => {
      if (d.trail.length < 2) return
      for (let i = 1; i < d.trail.length; i++) {
        const a = (i/d.trail.length)*0.7, w = (i/d.trail.length)*3.5
        ctx.beginPath()
        ctx.moveTo(d.trail[i-1].x, d.trail[i-1].y)
        ctx.lineTo(d.trail[i].x, d.trail[i].y)
        ctx.strokeStyle = crashed ? '#ef4444' : '#22c55e'
        ctx.globalAlpha = a; ctx.lineWidth = w; ctx.lineCap = 'round'
        ctx.stroke(); ctx.globalAlpha = 1
      }
      if (!crashed && d.trail.length > 2) {
        const l = d.trail[d.trail.length-1]
        const g = ctx.createRadialGradient(l.x,l.y,0,l.x,l.y,14)
        g.addColorStop(0,'rgba(34,197,94,0.5)'); g.addColorStop(1,'transparent')
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(l.x,l.y,14,0,Math.PI*2); ctx.fill()
      }
    }

    const loop = (now: number) => {
      const W = canvas.width, H = canvas.height
      const dt = Math.min((now - d.lastT)/1000, 0.05); d.lastT = now
      const { phase: ph } = stateRef.current

      ctx.clearRect(0,0,W,H)

      const bgGrad = ctx.createLinearGradient(0,0,0,H)
      if (d.redFlash > 0) { bgGrad.addColorStop(0,'#200808'); bgGrad.addColorStop(1,'#0d1508') }
      else if (ph === 'flying') { bgGrad.addColorStop(0,'#040810'); bgGrad.addColorStop(0.5,'#080f1a'); bgGrad.addColorStop(1,'#081408') }
      else { bgGrad.addColorStop(0,'#060810'); bgGrad.addColorStop(1,'#0a1020') }
      ctx.fillStyle = bgGrad; ctx.fillRect(0,0,W,H)

      if (d.redFlash > 0) {
        ctx.fillStyle = `rgba(239,68,68,${d.redFlash*0.3})`; ctx.fillRect(0,0,W,H)
        d.redFlash = Math.max(0, d.redFlash - dt*2.5)
      }

      if (ph === 'flying' || ph === 'crashed') {
        ctx.save(); ctx.strokeStyle = ph==='crashed'?'rgba(239,68,68,0.06)':'rgba(34,197,94,0.06)'; ctx.lineWidth=0.5
        for (let i=0;i<=12;i++){ctx.beginPath();ctx.moveTo(i*W/12,0);ctx.lineTo(i*W/12,H);ctx.stroke()}
        for (let j=0;j<=7;j++){ctx.beginPath();ctx.moveTo(0,j*H/7);ctx.lineTo(W,j*H/7);ctx.stroke()}
        ctx.restore()
      }

      d.stars.forEach(s => {
        s.tw += s.sp; const a = 0.3+Math.sin(s.tw)*0.3
        ctx.fillStyle = `rgba(255,255,255,${a})`; ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2); ctx.fill()
      })
      d.clouds.forEach(c => {
        if (ph==='flying'||ph==='crashed') c.x -= c.sp
        if (c.x+c.w<0) c.x = W+c.w
        ctx.save(); ctx.globalAlpha=c.a; ctx.fillStyle='white'
        ctx.beginPath(); ctx.ellipse(c.x,c.y,c.w,c.h,0,0,Math.PI*2); ctx.fill(); ctx.restore()
      })

      drawTrail(ph === 'crashed')

      d.smoke = d.smoke.filter(s=>s.life>0)
      d.smoke.forEach(s => {
        s.x+=s.vx; s.y+=s.vy; s.vy-=0.02; s.life-=s.decay
        const sr = s.r*(1+(1-s.life)*0.5)
        ctx.save(); ctx.globalAlpha=s.life*0.35
        ctx.fillStyle=`rgb(${s.g},${s.g},${s.g})`; ctx.beginPath(); ctx.arc(s.x,s.y,sr,0,Math.PI*2); ctx.fill(); ctx.restore()
      })

      d.particles = d.particles.filter(p=>p.life>0)
      d.particles.forEach(p => {
        p.x+=p.vx; p.y+=p.vy; p.vy+=0.2; p.life-=p.decay
        ctx.save(); ctx.globalAlpha=Math.max(0,p.life)
        ctx.fillStyle=p.col; ctx.beginPath(); ctx.arc(p.x,p.y,p.r*Math.max(0,p.life),0,Math.PI*2); ctx.fill(); ctx.restore()
      })

      if (ph === 'flying') {
        d.gt += dt
        const pos = getPos(d.gt, W, H)
        d.planeX=pos.x; d.planeY=pos.y; d.planeAngle=pos.a
        d.trail.push({x:pos.x,y:pos.y}); if(d.trail.length>70) d.trail.shift()
        drawPlane(pos.x, pos.y, pos.a, 1, 0, now)

      } else if (ph === 'crashed') {
        d.sputterT += dt

        if (d.crashPhase === 'sputter') {
          const sf = Math.min(d.sputterT/0.8, 1)
          const fl = Math.max(0, 1-sf*2.5)
          spawnSmoke(d.planeX, d.planeY, 2)
          drawPlane(d.planeX, d.planeY, d.planeAngle, fl, sf*6, now)
          if (d.sputterT > 0.8) {
            d.crashPhase = 'flyaway'; d.flyawayT = 0
            d.crashStartX=d.planeX; d.crashStartY=d.planeY; d.crashStartAngle=d.planeAngle
            spawnExplosion(d.planeX, d.planeY)
            d.redFlash = 1
          }

        } else if (d.crashPhase === 'flyaway') {
          d.flyawayT += dt
          const ft = Math.min(d.flyawayT/1.4, 1)
          const e  = ft*ft*ft
          const tx = d.crashStartX + e*W*0.7
          const ty = d.crashStartY - e*H*0.8
          const ta = d.crashStartAngle - e*1.0
          if (d.flyawayT < 1.2) {
            spawnSmoke(tx, ty, 2)
            drawPlane(tx, ty, ta, 0, 0, now)
          }
          if (d.flyawayT > 2 && !d.crashCompleteFired) {
            d.crashCompleteFired = true
            onCrashComplete?.()
          }
        }

      } else if (ph === 'betting') {
        const px=W*0.09, py=H*0.82+Math.sin(now/600)*4
        drawPlane(px, py, -0.12+Math.sin(now/400)*0.05, 1, 0, now)
      } else {
        drawPlane(W*0.09, H*0.82, -0.12, 1, 0, now)
      }

      rafRef.current = requestAnimationFrame(loop)
    }

    resize()
    window.addEventListener('resize', resize)
    rafRef.current = requestAnimationFrame(t => { d.lastT = t; loop(t) })
    return () => {
      window.removeEventListener('resize', resize)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    />
  )
}
